import type { CSSProperties, ReactNode } from 'react'
import { fmt } from '../lib/calendar'

/**
 * The Year's date plate (WP 10.5, Doug: "move the dates to the side, in a
 * small square like Today's, smaller; keep an icon where there is one").
 * Weekday over day over month — `Sat / 5 / Dec` — in a 44px square that
 * fills a `u-row--list` row's leading slot exactly, one step down from
 * Today's 60px `.ev-plate` column.
 *
 * `icon` takes the weekday's line, not a fourth one: a flight's plane says
 * what the row is, and the day and month still say when. Three lines is
 * what fits in 44px at the caption size; four would not.
 *
 * `hue` tints the paper the way `.ev-plate` tints it with a discipline
 * hue — 12% into `--paper-2` — and is the caller's to choose, because what
 * a row's colour means (a birthday, a flight) is the caller's knowledge.
 *
 * A composition over a `div` and `app.css`'s `.dplate`, not a primitive.
 * The text is `aria-hidden` and the date is said once in `aria-label`
 * instead, so a screen reader hears "Saturday 5 December", not "Sat 5 Dec"
 * read as three separate words.
 */
export function DatePlate({ on, icon, hue }: {
  /** ISO date, `YYYY-MM-DD`. */
  on: string
  icon?: ReactNode
  /** A colour token, e.g. `var(--p-travel)`. Omitted, the plate is plain
   *  `--paper-2`. */
  hue?: string
}) {
  const style = hue ? ({ '--dplate-hue': hue } as CSSProperties) : undefined
  return (
    <div className="dplate" style={style} role="img"
      aria-label={fmt(on, { weekday: 'long', day: 'numeric', month: 'long' })}>
      {icon
        ? <span className="dplate__icon" aria-hidden="true">{icon}</span>
        : <span className="dplate__wd" aria-hidden="true">{fmt(on, { weekday: 'short' })}</span>}
      <b aria-hidden="true">{Number(on.slice(8, 10))}</b>
      <span className="dplate__mo" aria-hidden="true">{fmt(on, { month: 'short' })}</span>
    </div>
  )
}
